import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Partner,
  PartnerCustomModelSummary,
  PartnerCustomModelTestRequest,
  PartnerCustomModelTestResult,
  PartnerCustomModelWrite,
} from '../../api.js';
import { describeError } from '../../errors.js';
import { useAsyncAction } from '../../hooks/index.js';
import type { AdminHttpClient } from '../../http.js';
import type { ThinkingLevel, ToastMessage } from '../../types.js';
import { parsePositiveInteger } from '../model-limit-input.js';
import { defaultCustomModelForm, type PartnerCustomModelFormState } from '../partners/constants.js';
import {
  editFormStateFrom,
  isTestResultStale,
  pricingRequestFrom,
  validatePricingInput,
  type TestSubjectSnapshot,
} from '../partners/custom-model-pricing-form.js';
import {
  partnerThinkingLevelOptions,
  partnerThinkingLevelSelection,
  partnerThinkingLevelsPayload,
  partnerThinkingLevelsSupported,
} from '../partners/partner-thinking-levels-form.js';
import {
  PartnerCustomModelsTab,
  type CustomModelTestEntry,
} from '../partners/PartnerCustomModelsTab.js';

interface EditBaseline {
  revision: number;
  providerBaseUrl: string;
  thinkingLevels?: ThinkingLevel[];
}

export function CustomModelDefinitionsPanel({
  http,
  onToast,
  onOpenDeployment,
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  onOpenDeployment: (partnerId: string) => void;
}) {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerId, setPartnerId] = useState('');
  const [customModels, setCustomModels] = useState<PartnerCustomModelSummary[]>([]);
  const [customModelForm, setCustomModelForm] =
    useState<PartnerCustomModelFormState>(defaultCustomModelForm);
  const [editingModelKey, setEditingModelKey] = useState<string>();
  const [editBaseline, setEditBaseline] = useState<EditBaseline>();
  const [testBusy, setTestBusy] = useState<'provider' | 'llm'>();
  const [testResults, setTestResults] = useState<
    Array<{ result: PartnerCustomModelTestResult; taken: TestSubjectSnapshot }>
  >([]);
  const modelRequestRef = useRef(0);
  const partnerIdRef = useRef(partnerId);
  partnerIdRef.current = partnerId;
  const { busy, run } = useAsyncAction(onToast, t);
  const translateError = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      options === undefined ? t(key) : t(key, options),
    [t]
  );
  const selectedPartner = partners.find((partner) => partner.id === partnerId);

  const loadPartners = useCallback(async () => {
    try {
      const next = await http.listPartners();
      setPartners(next);
      setPartnerId((current) =>
        current && next.some((partner) => partner.id === current) ? current : (next[0]?.id ?? '')
      );
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    }
  }, [http, onToast, translateError]);

  const loadModels = useCallback(async () => {
    const requestId = ++modelRequestRef.current;
    if (!partnerId) {
      setCustomModels([]);
      return;
    }
    try {
      const next = await http.listPartnerCustomModels(partnerId);
      if (modelRequestRef.current === requestId && partnerIdRef.current === partnerId) {
        setCustomModels(next);
      }
    } catch (error) {
      if (modelRequestRef.current === requestId && partnerIdRef.current === partnerId) {
        onToast({ tone: 'error', message: describeError(error, translateError) });
      }
    }
  }, [http, onToast, partnerId, translateError]);

  useEffect(() => {
    void loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    cancelEditCustomModel();
    void loadModels();
  }, [loadModels]);

  function registerCustomModel(event: FormEvent) {
    event.preventDefault();
    if (!selectedPartner) return;
    const contextWindow = parsePositiveInteger(customModelForm.contextWindow);
    const maxOutputTokens = parsePositiveInteger(customModelForm.maxOutputTokens);
    const modelKey = customModelForm.modelKey.trim();
    const providerApiKey = customModelForm.providerApiKey.trim();
    const providerBaseUrl = customModelForm.providerBaseUrl.trim();
    const litellmBaseUrl = customModelForm.litellmBaseUrl.trim();
    const hasCredentialInput = Boolean(providerApiKey || providerBaseUrl || litellmBaseUrl);
    const backendId =
      customModelForm.backendId.trim() ||
      (hasCredentialInput ? `bk_${selectedPartner.id}_${modelKey}` : '');
    if (!/^[a-z0-9][a-z0-9-]*$/.test(modelKey)) {
      onToast({ tone: 'error', message: t('partners.customModelKeyInvalid') });
      return;
    }
    if (!contextWindow || !maxOutputTokens) {
      onToast({ tone: 'error', message: t('partners.customModelLimitsInvalid') });
      return;
    }
    if (hasCredentialInput && (!providerApiKey || !providerBaseUrl)) {
      onToast({ tone: 'error', message: t('partners.customModelCredentialInvalid') });
      return;
    }
    const pricing = validatePricingInput(customModelForm);
    if (!pricing.ok) {
      onToast({
        tone: 'error',
        message: t('partners.customModelPricingError.' + pricing.error),
      });
      return;
    }
    const thinkingLevelOptions = partnerThinkingLevelOptions(customModelForm.capabilities, customModelForm.litellmModelName || customModelForm.modelKey, customModelForm.thinkingLevels);
    const thinkingLevelSelection = partnerThinkingLevelSelection(
      thinkingLevelOptions,
      customModelForm.thinkingLevels
    );
    if (
      partnerThinkingLevelsSupported(thinkingLevelOptions) &&
      customModelForm.thinkingLevels !== undefined &&
      thinkingLevelSelection.length === 0
    ) {
      onToast({ tone: 'error', message: t(customModelForm.thinkingLevels === undefined ? 'partners.customModelThinkingLevelsUnset' : 'partners.customModelThinkingLevelsRequired') });
      return;
    }
    const thinkingLevels = partnerThinkingLevelsPayload(
      thinkingLevelOptions,
      thinkingLevelSelection
    );
    void run(
      async () => {
        await http.registerPartnerCustomModel(selectedPartner.id, {
          modelKey,
          displayName: customModelForm.displayName.trim(),
          ...(customModelForm.description.trim()
            ? { description: customModelForm.description.trim() }
            : {}),
          protocol: 'openai-compatible',
          routeBackend: {
            backendId,
            protocol: 'openai-compatible',
            litellmModelName: customModelForm.litellmModelName.trim(),
          },
          capabilities: customModelForm.capabilities,
          ...(thinkingLevels ? { thinkingLevels } : {}),
          limits: { contextWindow, maxOutputTokens },
          pricing: pricingRequestFrom(pricing.pricing),
          status: 'active',
          ...(hasCredentialInput
            ? {
                providerCredential: {
                  apiKey: providerApiKey,
                  baseUrl: providerBaseUrl,
                  ...(litellmBaseUrl ? { litellmBaseUrl } : {}),
                },
              }
            : {}),
        });
        setCustomModelForm(defaultCustomModelForm());
        await loadModels();
      },
      { successKey: 'partners.customModelRegistered' }
    );
  }

  const customModelTestEntries: CustomModelTestEntry[] = testResults.map((entry) => ({
    result: entry.result,
    stale: isTestResultStale(entry.taken, {
      litellmModelName: customModelForm.litellmModelName,
      providerBaseUrl: customModelForm.providerBaseUrl,
      providerApiKey: customModelForm.providerApiKey,
      litellmBaseUrl: customModelForm.litellmBaseUrl,
    }),
  }));

  function testSubjectSnapshot(): TestSubjectSnapshot {
    return {
      litellmModelName: customModelForm.litellmModelName,
      providerBaseUrl: customModelForm.providerBaseUrl,
      providerApiKey: customModelForm.providerApiKey,
      litellmBaseUrl: customModelForm.litellmBaseUrl,
    };
  }

  function testRequest(): PartnerCustomModelTestRequest {
    const apiKey = customModelForm.providerApiKey.trim();
    const baseUrl = customModelForm.providerBaseUrl.trim();
    if (apiKey && baseUrl) {
      const litellmBaseUrl = customModelForm.litellmBaseUrl.trim();
      return {
        draft: {
          protocol: 'openai-compatible',
          litellmModelName: customModelForm.litellmModelName.trim(),
          baseUrl,
          apiKey,
          ...(litellmBaseUrl ? { litellmBaseUrl } : {}),
        },
      };
    }
    return { modelKey: (editingModelKey ?? customModelForm.modelKey).trim() };
  }

  function runCustomModelTest(kind: 'provider' | 'llm') {
    if (!selectedPartner) return;
    const taken = testSubjectSnapshot();
    const request = testRequest();
    setTestBusy(kind);
    void (async () => {
      try {
        const result =
          kind === 'provider'
            ? await http.testPartnerCustomModelProvider(selectedPartner.id, request)
            : await http.testPartnerCustomModelLlm(selectedPartner.id, request);
        setTestResults((current) => [
          ...current.filter((entry) => entry.result.kind !== result.kind),
          { result, taken },
        ]);
      } catch (error) {
        onToast({ tone: 'error', message: describeError(error, translateError) });
      } finally {
        setTestBusy(undefined);
      }
    })();
  }

  function beginEditCustomModel(modelKey: string) {
    if (!selectedPartner) return;
    const model = customModels.find((candidate) => candidate.modelKey === modelKey);
    if (!model) return;
    const form = editFormStateFrom(model, selectedPartner.id);
    if (form.blockedByBillingMode) {
      onToast({
        tone: 'error',
        message: t('partners.customModelEditBlockedByBillingMode'),
      });
      return;
    }
    setEditingModelKey(model.modelKey);
    setEditBaseline({
      revision: form.expectedRevision,
      providerBaseUrl: form.providerBaseUrl,
      ...(model.thinkingLevels === undefined
        ? {}
        : { thinkingLevels: [...model.thinkingLevels] }),
    });
    setCustomModelForm({
      modelKey: form.modelKey,
      displayName: form.displayName,
      description: form.description,
      backendId: form.backendId,
      litellmModelName: form.litellmModelName,
      providerApiKey: form.providerApiKey,
      providerBaseUrl: form.providerBaseUrl,
      litellmBaseUrl: form.litellmBaseUrl,
      contextWindow: form.contextWindow,
      maxOutputTokens: form.maxOutputTokens,
      inputUsdPerMillion: form.inputUsdPerMillion,
      outputUsdPerMillion: form.outputUsdPerMillion,
      cachedInputUsdPerMillion: form.cachedInputUsdPerMillion,
      cacheWriteInputUsdPerMillion: form.cacheWriteInputUsdPerMillion,
      marginMultiplier: form.marginMultiplier,
      capabilities: form.capabilities,
      ...(form.thinkingLevels === undefined ? {} : { thinkingLevels: form.thinkingLevels }),
    });
    setTestResults([]);
    if (form.legacyWithoutPricing) {
      onToast({ tone: 'error', message: t('partners.customModelLegacyPricingRequired') });
    }
  }

  function cancelEditCustomModel() {
    setEditingModelKey(undefined);
    setEditBaseline(undefined);
    setCustomModelForm(defaultCustomModelForm());
    setTestResults([]);
  }

  function saveCustomModelEdit() {
    if (!selectedPartner || !editingModelKey || !editBaseline) return;
    const contextWindow = parsePositiveInteger(customModelForm.contextWindow);
    const maxOutputTokens = parsePositiveInteger(customModelForm.maxOutputTokens);
    if (!contextWindow || !maxOutputTokens) {
      onToast({ tone: 'error', message: t('partners.customModelLimitsInvalid') });
      return;
    }
    const pricing = validatePricingInput(customModelForm);
    if (!pricing.ok) {
      onToast({
        tone: 'error',
        message: t('partners.customModelPricingError.' + pricing.error),
      });
      return;
    }
    const thinkingLevelOptions = partnerThinkingLevelOptions(customModelForm.capabilities, customModelForm.litellmModelName || customModelForm.modelKey, customModelForm.thinkingLevels);
    const thinkingLevelSelection = partnerThinkingLevelSelection(
      thinkingLevelOptions,
      customModelForm.thinkingLevels
    );
    if (
      partnerThinkingLevelsSupported(thinkingLevelOptions) &&
      customModelForm.thinkingLevels !== undefined &&
      thinkingLevelSelection.length === 0
    ) {
      onToast({ tone: 'error', message: t(customModelForm.thinkingLevels === undefined ? 'partners.customModelThinkingLevelsUnset' : 'partners.customModelThinkingLevelsRequired') });
      return;
    }
    const thinkingLevels = partnerThinkingLevelsPayload(
      thinkingLevelOptions,
      thinkingLevelSelection,
      editBaseline.thinkingLevels
    );
    const apiKey = customModelForm.providerApiKey.trim();
    const baseUrl = customModelForm.providerBaseUrl.trim();
    if (apiKey && !baseUrl) {
      onToast({ tone: 'error', message: t('partners.customModelCredentialInvalid') });
      return;
    }
    if (!apiKey && baseUrl !== editBaseline.providerBaseUrl.trim()) {
      onToast({ tone: 'error', message: t('partners.customModelBaseUrlNeedsKey') });
      return;
    }
    const litellmModelName = customModelForm.litellmModelName.trim();
    void run(
      async () => {
        await http.patchPartnerCustomModel(selectedPartner.id, editingModelKey, {
          expectedRevision: editBaseline.revision,
          patch: {
            displayName: customModelForm.displayName.trim(),
            ...(customModelForm.description.trim()
              ? { description: customModelForm.description.trim() }
              : {}),
            ...(litellmModelName ? { routeBackend: { litellmModelName } } : {}),
            capabilities: customModelForm.capabilities,
            ...(thinkingLevels ? { thinkingLevels } : {}),
            limits: { contextWindow, maxOutputTokens },
            pricing: pricingRequestFrom(pricing.pricing),
            ...(apiKey
              ? {
                  providerCredential: {
                    apiKey,
                    baseUrl,
                    ...(customModelForm.litellmBaseUrl.trim()
                      ? { litellmBaseUrl: customModelForm.litellmBaseUrl.trim() }
                      : {}),
                  },
                }
              : {}),
          },
        });
        cancelEditCustomModel();
        await loadModels();
      },
      { successKey: 'partners.customModelUpdated' }
    );
  }

  function deleteCustomModel(modelKey: string) {
    if (!selectedPartner) return;
    if (!window.confirm(t('partners.confirmDeleteCustomModel', { modelKey }))) return;
    void run(
      async () => {
        await http.deletePartnerCustomModel(selectedPartner.id, modelKey);
        await loadModels();
      },
      { successKey: 'partners.customModelDeleted' }
    );
  }

  function toggleCustomModelCapability(
    key: keyof PartnerCustomModelWrite['capabilities'],
    checked: boolean
  ) {
    setCustomModelForm((current) => {
      const capabilities = { ...current.capabilities, [key]: checked };
      return {
        ...current,
        capabilities,
        ...(key === 'reasoning'
          ? { thinkingLevels: partnerThinkingLevelOptions(capabilities) }
          : {}),
      };
    });
  }

  return (
    <section className="content-stack custom-model-definitions">
      <div className="panel section-heading">
        <div>
          <h2>{t('platform.customModelDefinitions')}</h2>
          <span className="badge">{t('platform.definitionKindDedicated')}</span>
        </div>
        <div className="toolbar-form">
          <label>
            {t('platform.customModelPartner')}
            <select
              value={partnerId}
              disabled={busy}
              onChange={(event) => setPartnerId(event.target.value)}
            >
              <option value="">{t('platform.selectCustomModelPartner')}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name} ({partner.id})
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="secondary"
            disabled={!partnerId}
            onClick={() => onOpenDeployment(partnerId)}
          >
            {t('platform.openCustomModelDeployment')}
          </button>
        </div>
      </div>
      {selectedPartner ? (
        <PartnerCustomModelsTab
          customModels={customModels}
          customModelForm={customModelForm}
          busy={busy}
          {...(testBusy === undefined ? {} : { testBusy })}
          testResults={customModelTestEntries}
          {...(editingModelKey === undefined ? {} : { editingModelKey })}
          ownerLabel={`${selectedPartner.name} (${selectedPartner.id})`}
          kindLabel={t('platform.definitionKindDedicated')}
          setCustomModelForm={setCustomModelForm}
          onRegister={registerCustomModel}
          onEdit={beginEditCustomModel}
          onCancelEdit={cancelEditCustomModel}
          onSaveEdit={saveCustomModelEdit}
          onTestProvider={() => runCustomModelTest('provider')}
          onTestLlm={() => runCustomModelTest('llm')}
          onDelete={deleteCustomModel}
          onToggleCapability={toggleCustomModelCapability}
        />
      ) : (
        <p className="panel muted">{t('platform.selectCustomModelPartner')}</p>
      )}
    </section>
  );
}
